/**
 * GraphQL user API: getUser, getAllUsers, updateUser, inviteUser.
 * ASSUMPTION: Lambdas return ApiResponse { statusCode, body } with JSON body; list shape may use items, users, or a top-level array.
 */

import { graphqlRequest } from "@/graphql/client";
import { updateUser } from "@/graphql/mutations";
import { getAllUsers, getUser, inviteUser } from "@/graphql/queries";
import { validatePagination } from "@/store/propertyCore/propertyCacheUtils";
import { IS_DEV } from "@/utils/env";
import { getUserProfileNameParts } from "@/utils/getUserProfileNameParts";
import { mergeRealtorAgentIntoMetadata, isRealtorAgentComplete } from "@/utils/realtorAgentMetadata";
import { normalizePhoneForBackend } from "@/utils/usPhone";

const AUTH_MODE = "AMAZON_COGNITO_USER_POOLS";

/** Safety cap on getAllUsers pagination loops. */
const USER_DIRECTORY_MAX_PAGES = 50;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

/**
 * AppSync GraphQL often rejects with `{ errors: [{ message }] }`; `Error.message` may be "[object Object]".
 *
 * @param {unknown} err
 * @returns {string}
 */
function formatGraphqlOrNetworkError(err) {
  if (err == null) return "";
  if (typeof err === "string") return err;

  const o = typeof err === "object" ? err : null;
  if (o) {
    const graphErrors = o.errors;
    if (Array.isArray(graphErrors) && graphErrors.length > 0) {
      const parts = graphErrors.map((x) => {
        if (x == null) return "";
        if (typeof x.message === "string") return x.message;
        try {
          return JSON.stringify(x);
        } catch {
          return String(x);
        }
      });
      const joined = parts.filter(Boolean).join("; ");
      if (joined) return joined;
    }
  }

  if (err instanceof Error) {
    const m = String(err.message ?? "").trim();
    if (m && m !== "[object Object]") return m;
  }

  if (o) {
    const m = typeof o.message === "string" ? o.message.trim() : "";
    if (m && m !== "[object Object]") return m;
    if (o.message != null && typeof o.message === "object") {
      try {
        return JSON.stringify(o.message);
      } catch {
        return String(o.message);
      }
    }
    try {
      return JSON.stringify(o);
    } catch {
      return String(err);
    }
  }

  return String(err);
}

/** Lambda JSON body: `message` or string `error` (inviteUser / user-put). */
function messageFromLambdaBody(parsed) {
  if (parsed == null) return "";
  if (typeof parsed === "string") return parsed.trim();
  if (typeof parsed !== "object") return "";
  const o = /** @type {{ message?: unknown; error?: unknown }} */ (parsed);
  if (typeof o.message === "string" && o.message.trim()) return o.message.trim();
  if (typeof o.error === "string" && o.error.trim()) return o.error.trim();
  return "";
}

/**
 * @param {object|null|undefined} payload - getAllUsers / updateUser GraphQL field payload
 * @returns {{ ok: boolean, parsed: unknown, statusCode: string }}
 */
function parseApiResponsePayload(payload) {
  if (payload == null || typeof payload !== "object") {
    return { ok: false, parsed: null, statusCode: "" };
  }
  const statusCode = String(payload.statusCode ?? "");
  let parsed;
  try {
    parsed = typeof payload.body === "string" ? JSON.parse(payload.body) : payload.body;
  } catch {
    return { ok: false, parsed: null, statusCode };
  }
  const ok = statusCode === "200" && parsed != null;
  return { ok, parsed, statusCode };
}

/**
 * Parses the `getUser` field from a GraphQL `data` object (single-user profile).
 *
 * @param {object|null|undefined} data - `response.data` from `graphqlRequest`
 * @returns {{ ok: boolean, body: object|null, statusCode: string }}
 */
export function parseGetUserResponse(data) {
  const r = parseApiResponsePayload(data?.getUser);
  const p = r.parsed;
  const isPlainObject = p != null && typeof p === "object" && !Array.isArray(p);
  if (!r.ok || !isPlainObject) {
    return { ok: false, body: null, statusCode: r.statusCode };
  }
  return { ok: true, body: p, statusCode: r.statusCode };
}

/**
 * Fetches one user profile by email (`getUser`).
 *
 * @param {string} email
 * @param {string} [customerId] - Optional tenant scope for the resolver
 * @returns {Promise<{ ok: boolean, body: object|null, statusCode: string }>}
 */
export async function getUserByEmail(email, customerId) {
  const em = String(email ?? "")
    .trim()
    .toLowerCase();
  if (!EMAIL_RE.test(em)) {
    return { ok: false, body: null, statusCode: "" };
  }
  /** @type {{ email: string, customer_id?: string }} */
  const variables = { email: em };
  const cust = String(customerId ?? "").trim();
  if (cust) variables.customer_id = cust;
  try {
    const response = await graphqlRequest({
      query: getUser,
      variables,
      authMode: AUTH_MODE,
    });
    return parseGetUserResponse(response?.data);
  } catch {
    return { ok: false, body: null, statusCode: "" };
  }
}

/**
 * @param {unknown} parsed - Parsed JSON body from getAllUsers
 * @returns {object[]}
 */
function extractUsersFromListBody(parsed) {
  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === "object") {
    const o = parsed;
    if (Array.isArray(o.users)) return o.users;
    if (Array.isArray(o.items)) return o.items;
    if (Array.isArray(o.data)) return o.data;
  }
  return [];
}

/**
 * @param {unknown} parsed
 * @returns {object|string|null|undefined}
 */
function extractNextPageFromListBody(parsed) {
  if (parsed == null || typeof parsed !== "object") return null;
  const o = parsed;
  return o.next_page ?? o.nextPage ?? o.next_token ?? o.nextToken ?? null;
}

/**
 * @param {unknown} token
 * @returns {boolean}
 */
function isUsableLegacyNextPageToken(token) {
  if (token == null || token === "") return false;
  if (typeof token === "object" && token !== null && Object.keys(token).length === 0) return false;
  return true;
}

/**
 * Same cursor semantics as {@link fetchPropertiesFromApi} / PropertyList: optional nested `pagination`
 * from the Lambda body, else legacy top-level `next_page` fields.
 *
 * @param {unknown} parsed
 * @returns {ReturnType<typeof validatePagination>|null}
 */
function getUserListPagination(parsed) {
  if (parsed == null || typeof parsed !== "object") return null;
  const nested = validatePagination(parsed.pagination);
  if (nested) return nested;
  const legacyToken = extractNextPageFromListBody(parsed);
  if (!isUsableLegacyNextPageToken(legacyToken)) return null;
  return {
    page: null,
    next_page: legacyToken,
    limit: null,
    total_pages: null,
    has_next: true,
    has_previous: false,
  };
}

/**
 * Matches PropertyList `hasMore` (see `src/pages/PropertyList/index.jsx`).
 *
 * @param {ReturnType<typeof validatePagination>} pagination
 * @returns {boolean}
 */
function paginationIndicatesMorePages(pagination) {
  if (!pagination) return false;
  return Boolean(pagination.next_page) && (pagination.has_next === true || pagination.has_next === "true");
}

/**
 * Aligns with {@link fetchPropertiesFromApi}: AWSJSON `next_page` is sent as a string when the token is not already a string.
 *
 * @param {unknown} token
 * @returns {string|undefined}
 */
function serializeNextPageForGraphql(token) {
  if (token === null || token === undefined || token === "") return undefined;
  return typeof token === "string" ? token : JSON.stringify(token);
}

/**
 * @param {object} raw
 * @returns {{ id: string, email: string, displayName: string, initial: string }|null}
 */
function mapRawUserToPickerRow(raw) {
  if (raw == null || typeof raw !== "object") return null;
  const email = String(raw.email ?? "")
    .trim()
    .toLowerCase();
  if (!EMAIL_RE.test(email)) return null;
  const first = String(raw.first_name ?? raw.firstname ?? raw.firstName ?? "").trim();
  const last = String(raw.last_name ?? raw.lastname ?? raw.lastName ?? "").trim();
  const displayName = `${first} ${last}`.trim() || String(raw.name ?? "").trim() || email;
  const userId = String(raw.username ?? raw.user_id ?? raw.id ?? "").trim();
  return {
    id: userId || email,
    email,
    displayName,
    initial: (displayName.charAt(0) || "?").toUpperCase(),
  };
}

/**
 * One entry from `groups` / Cognito-ish lists: string slug, `{ name }` / `{ role }`, or Dynamo-style `{ S }`.
 *
 * @param {unknown} x
 * @returns {string}
 */
function roleSlugFromGroupEntry(x) {
  if (x == null || x === "") return "";
  if (typeof x === "string") {
    const s = x.trim().toLowerCase();
    return s;
  }
  if (typeof x === "object" && x !== null) {
    const o = /** @type {Record<string, unknown>} */ (x);
    if (typeof o.name === "string" && o.name.trim()) return o.name.trim().toLowerCase();
    if (typeof o.role === "string" && o.role.trim()) return o.role.trim().toLowerCase();
    if (typeof o.group === "string" && o.group.trim()) return o.group.trim().toLowerCase();
    /** Dynamo AttributeValue `{ S: "realtor" }` when present unmigrated */
    if (typeof o.S === "string" && o.S.trim()) return o.S.trim().toLowerCase();
    if (typeof o.id === "string" && o.id.trim()) return o.id.trim().toLowerCase();
  }
  return "";
}

/**
 * Reads `groups` / `cognito_groups` / `cognito:groups` from a getUser-ish record into lowercase role slugs.
 * Imported by {@link actingUserApi}; do not stringify unknown objects (`[object Object]`).
 *
 * @param {object} raw
 * @returns {string[]}
 */
export function normalizeRolesFromUserRecord(raw) {
  if (raw == null || typeof raw !== "object") return [];
  const r = raw.groups ?? raw.cognito_groups ?? raw["cognito:groups"];
  if (Array.isArray(r)) {
    return r.map((x) => roleSlugFromGroupEntry(x)).filter(Boolean);
  }
  if (typeof r === "string") {
    const s = r.trim();
    if (!s) return [];
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) {
        return parsed.map((x) => roleSlugFromGroupEntry(x)).filter(Boolean);
      }
    } catch {
      /* comma-separated or single token */
    }
    return s
      .split(/[,\s]+/)
      .map((x) => x.trim().toLowerCase())
      .filter(Boolean);
  }
  return [];
}

/**
 * @param {object} raw
 * @returns {{ id: string, email: string, displayName: string, initial: string, phone: string, roles: string[], lastActivityAt: Date|null }|null}
 */
function mapRawUserToDashboardRow(raw) {
  const base = mapRawUserToPickerRow(raw);
  if (!base) return null;
  const phoneRaw = String(raw.phone ?? raw.phone_number ?? "").trim();
  const roles = normalizeRolesFromUserRecord(raw);
  const lastRaw = raw.last_activity ?? raw.last_login ?? raw.last_modified ?? raw.updated_at ?? raw.insert_date ?? null;
  let lastActivityAt = null;
  if (lastRaw != null && lastRaw !== "") {
    const d = new Date(lastRaw);
    if (!Number.isNaN(d.getTime())) lastActivityAt = d;
  }
  return {
    ...base,
    phone: phoneRaw || "—",
    roles,
    lastActivityAt,
  };
}

/**
 * @param {string} customerId
 * @param {object} [options]
 * @param {boolean} [options.active]
 * @returns {Promise<Map<string, object>>}
 */
async function loadAllTenantUsersRawByEmail(customerId, options = {}) {
  const cust = String(customerId ?? "").trim();
  if (!cust) return new Map();

  if (IS_DEV) {
    console.log("[userApi] getAllUsers start", {
      customer_id: cust,
      active: typeof options.active === "boolean" ? options.active : undefined,
    });
  }

  let nextPageToken = null;
  /** @type {Map<string, object>} */
  const byEmail = new Map();
  let pages = 0;
  let requestCount = 0;

  while (pages < USER_DIRECTORY_MAX_PAGES) {
    /** @type {Record<string, unknown>} */
    const variables = {
      customer_id: cust,
    };
    if (typeof options.active === "boolean") {
      variables.active = options.active;
    }
    const serializedNext = serializeNextPageForGraphql(nextPageToken);
    if (serializedNext !== undefined) {
      variables.next_page = serializedNext;
    }

    let response;
    try {
      response = await graphqlRequest({
        query: getAllUsers,
        variables,
        authMode: AUTH_MODE,
      });
      requestCount += 1;
    } catch (err) {
      const detail = formatGraphqlOrNetworkError(err);
      if (IS_DEV) {
        console.error("[userApi] getAllUsers GraphQL request failed", {
          customer_id: cust,
          page: pages,
          message: detail || "(no message)",
          raw: err,
        });
        console.log("[userApi] getAllUsers failed (GraphQL)", {
          customer_id: cust,
          page: pages,
          message: detail || "(no message)",
        });
      }
      throw new Error(detail ? `getAllUsers request failed: ${detail}` : "getAllUsers request failed");
    }

    const { ok, parsed, statusCode } = parseApiResponsePayload(response?.data?.getAllUsers);
    if (!ok) {
      if (IS_DEV) {
        console.log("[userApi] getAllUsers non-OK response", {
          customer_id: cust,
          page: pages,
          statusCode,
          isFirstPage: pages === 0,
          rawResponse: response,
        });
      }
      if (pages === 0) {
        throw new Error(statusCode ? `getAllUsers failed (${statusCode})` : "getAllUsers failed");
      }
      break;
    }

    const batch = extractUsersFromListBody(parsed);
    for (const u of batch) {
      const email = String(u?.email ?? "")
        .trim()
        .toLowerCase();
      if (!EMAIL_RE.test(email)) continue;
      if (!byEmail.has(email)) byEmail.set(email, u);
    }

    const listPagination = getUserListPagination(parsed);
    const hasNext = paginationIndicatesMorePages(listPagination);

    if (IS_DEV) {
      console.log("[userApi] getAllUsers page", {
        customer_id: cust,
        page: pages,
        rawBatchSize: batch.length,
        uniqueUsersSoFar: byEmail.size,
        hasNextPage: hasNext,
      });
    }

    if (!hasNext) break;
    nextPageToken = listPagination?.next_page ?? null;
    pages += 1;
  }

  if (IS_DEV) {
    console.log("[userApi] getAllUsers done", {
      customer_id: cust,
      requests: requestCount,
      uniqueUsers: byEmail.size,
    });
  }

  return byEmail;
}

/**
 * Loads all pages of getAllUsers for the superadmin user picker (local search in the modal).
 *
 * @param {string} customerId
 * @param {object} [options]
 * @param {boolean} [options.active] - When set, filter to active/inactive; when omitted, backend default applies.
 * @returns {Promise<Array<{ id: string, email: string, displayName: string, initial: string }>>}
 */
export async function fetchTenantUsersForPicker(customerId, options = {}) {
  const byEmail = await loadAllTenantUsersRawByEmail(customerId, options);
  return Array.from(byEmail.values())
    .map((raw) => mapRawUserToPickerRow(raw))
    .filter(Boolean)
    .sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" }));
}

/**
 * Same tenant directory as {@link fetchTenantUsersForPicker}, with fields needed for the Users dashboard table.
 *
 * @param {string} customerId
 * @param {object} [options]
 * @param {boolean} [options.active]
 * @returns {Promise<Array<{ id: string, email: string, displayName: string, initial: string, phone: string, roles: string[], lastActivityAt: Date|null }>>}
 */
export async function fetchTenantUsersForDashboard(customerId, options = {}) {
  const byEmail = await loadAllTenantUsersRawByEmail(customerId, options);
  return Array.from(byEmail.values())
    .map((raw) => mapRawUserToDashboardRow(raw))
    .filter(Boolean)
    .sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" }));
}

/**
 * Partial updateUser mutation. Only includes arguments that are not `undefined`.
 *
 * @param {object} params
 * @param {string} params.userId
 * @param {string} [params.firstName]
 * @param {string} [params.lastName]
 * @param {string} [params.phone]
 * @param {string} [params.email]
 * @param {boolean} [params.active]
 * @param {object} [params.metadata] - Serialized as AWSJSON
 * @param {string} [params.customerId]
 * @param {string[]} [params.groups]
 * @returns {Promise<{ ok: boolean, statusCode: string, body: unknown }>}
 */
export async function updateUserProfile(params) {
  const userId = String(params?.userId ?? "").trim();
  if (!userId) {
    return { ok: false, statusCode: "", body: null };
  }

  /** @type {Record<string, unknown>} */
  const variables = { user_id: userId };
  if (params.firstName !== undefined) variables.first_name = params.firstName;
  if (params.lastName !== undefined) variables.last_name = params.lastName;
  if (params.phone !== undefined) {
    const normalizedPhone = normalizePhoneForBackend(params.phone);
    variables.phone = normalizedPhone ?? "";
  }
  if (params.email !== undefined) variables.email = params.email;
  if (params.active !== undefined) variables.active = params.active;
  if (params.metadata !== undefined) variables.metadata = params.metadata;
  if (params.customerId !== undefined) variables.customer_id = params.customerId;
  if (params.groups !== undefined) variables.groups = normalizeGroupsList(params.groups);

  let response;
  try {
    response = await graphqlRequest({
      query: updateUser,
      variables,
      authMode: AUTH_MODE,
    });
  } catch {
    return { ok: false, statusCode: "", body: null };
  }

  const { ok, parsed, statusCode } = parseApiResponsePayload(response?.data?.updateUser);
  return { ok, statusCode, body: parsed };
}

/**
 * Builds updateUser profile fields from loaded profile + auth fallbacks.
 * ASSUMPTION: AppSync forwards null for omitted GraphQL args; update_user rejects
 * null string keys, so callers must send full string profile fields on every update.
 *
 * @param {unknown} userProfile
 * @param {{ firstName?: string, lastName?: string, phone?: string, email?: string, active?: boolean }} [fallbacks]
 * @returns {{ firstName: string, lastName: string, phone: string, email: string, active?: boolean }}
 */
function resolveUserProfileFieldsForUpdate(userProfile, fallbacks = {}) {
  const profile = userProfile && typeof userProfile === "object" ? userProfile : {};
  const { first, last } = getUserProfileNameParts(profile);
  const firstName = first || String(fallbacks.firstName ?? "").trim();
  const lastName = last || String(fallbacks.lastName ?? "").trim();
  const phone = normalizePhoneForBackend(profile.phone ?? fallbacks.phone) ?? "";
  const email = String(profile.email ?? fallbacks.email ?? "")
    .trim()
    .toLowerCase();
  const active =
    typeof profile.active === "boolean"
      ? profile.active
      : typeof fallbacks.active === "boolean"
        ? fallbacks.active
        : undefined;
  return active === undefined
    ? { firstName, lastName, phone, email }
    : { firstName, lastName, phone, email, active };
}

/**
 * Merges realtor agent fields into existing user metadata and persists via updateUser.
 *
 * @param {object} params
 * @param {string} params.userId
 * @param {string} [params.customerId]
 * @param {import("@/utils/realtorAgentMetadata").RealtorAgent} params.realtorAgent
 * @param {unknown} [params.existingMetadata]
 * @param {unknown} [params.userProfile]
 * @param {{ firstName?: string, lastName?: string, phone?: string, email?: string, active?: boolean }} [params.profileFallbacks]
 * @returns {Promise<{ ok: boolean, statusCode: string, body: unknown, metadata: object|null }>}
 */
export async function saveRealtorAgentToUserProfile({
  userId,
  customerId,
  realtorAgent,
  existingMetadata,
  userProfile,
  profileFallbacks,
}) {
  const resolvedUserId = String(userId ?? "").trim();
  if (!resolvedUserId) {
    return { ok: false, statusCode: "", body: null, metadata: null };
  }

  const metadata = mergeRealtorAgentIntoMetadata(existingMetadata, realtorAgent);
  if (!isRealtorAgentComplete(metadata.realtor)) {
    return { ok: false, statusCode: "", body: null, metadata: null };
  }
  const profileFields = resolveUserProfileFieldsForUpdate(userProfile, profileFallbacks);
  const result = await updateUserProfile({
    userId: resolvedUserId,
    customerId,
    metadata: JSON.stringify(metadata),
    ...profileFields,
  });
  return { ...result, metadata: result.ok ? metadata : null };
}

/**
 * Normalizes invite/update role input to a non-empty string array for GraphQL `groups`.
 *
 * @param {string|string[]|undefined} groups
 * @returns {string[]}
 */
function normalizeGroupsList(groups) {
  if (!Array.isArray(groups)) return [];
  return groups.map((role) => String(role ?? "").trim()).filter(Boolean);
}

/**
 * `Query.inviteUser` — invites/creates via `dev-cai-web-user-put` (same as Sign Up Invitation).
 *
 * @param {object} params
 * @param {string} params.firstName
 * @param {string} params.lastName
 * @param {string} params.phone
 * @param {string} params.email
 * @param {string} params.accountId - Passed as GraphQL `account`
 * @param {string[]} params.groups - One or more role ids
 * @returns {Promise<{ ok: boolean, statusCode: string, body: unknown }>}
 */
export async function inviteTenantUser(params) {
  const firstname = String(params?.firstName ?? "").trim();
  const lastname = String(params?.lastName ?? "").trim();
  const phone = normalizePhoneForBackend(params?.phone);
  const email = String(params?.email ?? "")
    .trim()
    .toLowerCase();
  const account = String(params?.accountId ?? "").trim();
  const groups = normalizeGroupsList(params?.groups);
  if (!firstname || !lastname || !phone || !email || !account || groups.length === 0) {
    return { ok: false, statusCode: "", body: null };
  }

  let response;
  try {
    response = await graphqlRequest({
      query: inviteUser,
      variables: { firstname, lastname, phone, email, account, groups },
      authMode: AUTH_MODE,
    });
  } catch (err) {
    console.log("inviteTenantUser failed", err);
    return { ok: false, statusCode: "", body: { message: formatGraphqlOrNetworkError(err) } };
  }

  const { ok, parsed, statusCode } = parseApiResponsePayload(response?.data?.inviteUser);

  if (!ok) {
    const gqlErr =
      Array.isArray(response?.errors) && response.errors.length > 0
        ? formatGraphqlOrNetworkError({ errors: response.errors })
        : "";
    const fromBody = messageFromLambdaBody(parsed);
    const text = [fromBody, gqlErr].filter(Boolean).join(fromBody && gqlErr ? " — " : "");
    console.log("inviteTenantUser failed", { statusCode, parsed, errors: response?.errors });
    return { ok: false, statusCode, body: text ? { message: text } : parsed };
  }

  return { ok, statusCode, body: parsed };
}
